import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, computed, effect, input, output, signal } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { EmailConfirmStatus, UserDTO } from '../../domain/user/user.model';

import { MatButton } from '@angular/material/button';
import { EmailUpdateComponent } from '../email-update/email-update.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dottnet-mail-confirm',
  templateUrl: 'mail-confirm.component.html',
  styleUrls: ['mail-confirm.component.scss'],
  imports: [MatButton, TranslatePipe, EmailUpdateComponent],
})
export class MailConfirmComponent implements OnInit, OnDestroy {
  @ViewChild(EmailUpdateComponent) emailUpdateComponent: EmailUpdateComponent;

  readonly sendMailEvent = output();
  readonly dismissEvent = output();
  readonly submitNewEmail = output<string>();

  resendCooldown = signal<number>(0);
  showEmailEdit = signal(false);
  private cooldownInterval: ReturnType<typeof setInterval> | null = null;

  readonly user = input<UserDTO | null>(null);
  readonly emailConfirmationStatus = input<EmailConfirmStatus | null>(null);

  readonly isLocking = computed(() =>
    this.emailConfirmationStatus() === EmailConfirmStatus.ShowLockingBanner
  );

  readonly isLinkExpired = computed(() =>
    this.emailConfirmationStatus() === EmailConfirmStatus.LinkExpired
  );

  title = signal<string>('');
  mailConfirmationText = signal<string>('');

  constructor(
    private ts: TranslateService,
  ) {
    effect(() => {
      const email = this.user()?.email || '';
      const status = this.emailConfirmationStatus();
      this.title.set(this.ts.instant('dottnet.emailConfirm.title'));
      if (status === EmailConfirmStatus.LinkExpired) {
        this.mailConfirmationText.set(this.ts.instant('dottnet.confirmMail.text.expired'));
      } else if (status === EmailConfirmStatus.ShowLockingBanner) {
        this.mailConfirmationText.set(this.ts.instant('dottnet.emailConfirm.body.locking', { email }));
      } else {
        this.mailConfirmationText.set(this.ts.instant('dottnet.emailConfirm.body.free', { email }));
      }
    });
  }

  ngOnInit(): void {
    this.title.set(this.ts.instant('dottnet.emailConfirm.title'));
    const email = this.user()?.email || '';
    const status = this.emailConfirmationStatus();
    if (status === EmailConfirmStatus.LinkExpired) {
      this.mailConfirmationText.set(this.ts.instant('dottnet.confirmMail.text.expired'));
    } else if (status === EmailConfirmStatus.ShowLockingBanner) {
      this.mailConfirmationText.set(this.ts.instant('dottnet.emailConfirm.body.locking', { email }));
    } else {
      this.mailConfirmationText.set(this.ts.instant('dottnet.emailConfirm.body.free', { email }));
    }
  }

  ngOnDestroy(): void {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  }

  onResendClick() {
    this.sendMailEvent.emit();
    this.startResendCooldown();
  }

  onUpdateEmailClick() {
    this.showEmailEdit.set(true);
  }

  onCancelEdit() {
    this.showEmailEdit.set(false);
  }

  onEmailSaved(newEmail: string) {
    this.submitNewEmail.emit(newEmail);
  }

  setMailExistenceError() {
    this.emailUpdateComponent?.setMailExistenceError();
  }

  private startResendCooldown() {
    this.resendCooldown.set(60);
    this.cooldownInterval = setInterval(() => {
      const current = this.resendCooldown();
      if (current <= 1) {
        this.resendCooldown.set(0);
        if (this.cooldownInterval) {
          clearInterval(this.cooldownInterval);
          this.cooldownInterval = null;
        }
      } else {
        this.resendCooldown.set(current - 1);
      }
    }, 1000);
  }
}
